import React from "react";
import { css } from "@emotion/react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import useAppSelector from "@hooks/useAppSelector";
import { selectCurrentUserId, selectUserById } from "@store/user/userSelectors";
import { WorkLog } from "@store/issue/types";
import { getLanguage } from "@helpers";

interface IssueWorkLogRowProps {
  workLog: WorkLog;
}

const IssueWorkLogRow: React.FC<IssueWorkLogRowProps> = ({
  workLog: { userId, remark, createdAt, prevState, nextState },
}) => {
  const { t } = useTranslation();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const user = useAppSelector(({ user }) => selectUserById(user, userId));

  const formattedDate = format(
    new Date(createdAt),
    "EEEEEE dd MMMM yyyy - HH:mm",
    {
      locale: getLanguage(),
    },
  );

  return (
    <div css={styles.logRow}>
      <div css={styles.initials}>
        <span css={styles.initialsLabel}>{user?.profile.initials}</span>
      </div>
      <div>
        <div css={styles.status}>
          <span css={styles.label}>{t(`issue.states.${prevState}`)}</span>
          <span>{"  >  "}</span>
          <span css={styles.label}>{t(`issue.states.${nextState}`)}</span>
        </div>
        {remark && (
          <span style={{ marginBottom: theme.sizing.scale200 }}>{remark}</span>
        )}
        <div css={styles.timestamp}>
          <span>{`${formattedDate} door ${
            user?.id === currentUserId ? "mij" : user?.profile.name
          }`}</span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  initials: css({
    display: "flex",
    backgroundColor: theme.colors.mono200,
    height: 42,
    width: 42,
    borderRadius: theme.borders.radius600,
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.sizing.scale400,
  }),
  logRow: css({
    display: "flex",
    marginTop: theme.sizing.scale200,
    marginBottom: theme.sizing.scale800,
    alignItems: "center",
  }),
  status: css({
    marginBottom: theme.sizing.scale200,
  }),
  timestamp: css({
    display: "flex",
    alignItems: "center",
  }),
  initialsLabel: css({
    color: theme.colors.white,
  }),
  label: css({
    borderRadius: theme.borders.radius400,
    padding: `${theme.sizing.scale200} ${theme.sizing.scale400}`,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.colors.mono200,
  }),
};

export default IssueWorkLogRow;
